import { useEffect, useRef, useState } from "react";
import { Speaker, Lightbulb, Wand2, MapPin } from "lucide-react";
import { SITE } from "../lib/api";

const stats = [
  { value: 200, suffix: "+", label: "événements animés" },
  { value: 4, suffix: "", label: "départements couverts" },
  { value: 98, suffix: "%", label: "satisfaction client" },
  { value: 365, suffix: "j", label: "disponibilités annuelles" },
];

function useCountUp(target, durationMs = 1600) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    let started = false;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !started) {
          started = true;
          const start = performance.now();
          const tick = (now) => {
            const p = Math.min((now - start) / durationMs, 1);
            const eased = 1 - Math.pow(1 - p, 3);
            setVal(Math.round(target * eased));
            if (p < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }
      });
    }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [target, durationMs]);
  return [val, ref];
}

function Stat({ value, suffix, label, idx }) {
  const [v, ref] = useCountUp(value);
  return (
    <div ref={ref} className="reveal" style={{ transitionDelay: `${idx * 80}ms` }} data-testid={`stat-${idx}`}>
      <div className="font-display text-5xl md:text-6xl text-white">
        {v}
        <span className="text-[#3B82F6]">{suffix}</span>
      </div>
      <div className="mt-2 text-xs uppercase tracking-[0.25em] text-zinc-400">{label}</div>
    </div>
  );
}

const features = [
  { icon: Speaker, title: "Sonorisation Pro", desc: "Matériel haut de gamme — Pioneer, JBL, RCF — calibré pour chaque salle." },
  { icon: Lightbulb, title: "Light Show", desc: "Lyres motorisées, lasers, machine à fumée et éclairage scène premium. Possibilité de réalisation de show Light en direct sur-mesure." },
  { icon: Wand2, title: "Animation sur-mesure", desc: "Programmation personnalisée — du cocktail au dancefloor jusqu'au bout de la nuit." },
  { icon: MapPin, title: "Nouvelle-Aquitaine", desc: "Déplacements 16, 17, 79, 86 et zones limitrophes Charente — devis et logistique inclus." },
];

export default function About() {
  return (
    <section id="about" className="relative py-28 lg:py-40 bg-[#050505]" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          {/* Left */}
          <div className="lg:col-span-5 reveal">
            <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6]">À propos</div>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-[0.95]">
              L'expérience<br />
              <span className="text-[#3B82F6]">événementielle</span><br />
              haut de gamme.
            </h2>
            <p className="mt-8 text-zinc-300 leading-relaxed max-w-xl">
              <span className="text-white font-semibold">Anton Pears</span> propose des prestations DJ premium pour tous types d'événements privés et professionnels. De l'ouverture de bal à la dernière piste, chaque soirée est pensée comme une expérience unique — son cristallin, lumières immersives, ambiance maîtrisée.
            </p>
            <p className="mt-4 text-zinc-400 leading-relaxed max-w-xl">
              Basé en Charente, déclaré et assuré, Anton intervient sur l'ensemble de la Nouvelle-Aquitaine avec un matériel professionnel rigoureusement entretenu et une écoute attentive de chaque demande.
            </p>
          </div>

          {/* Right - photo card */}
          <div className="lg:col-span-7 reveal">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 group">
              <img
                src="https://customer-assets.emergentagent.com/job_anton-pears-dj/artifacts/wu83vu3z_IMG_5703.jpg"
                alt="Setup professionnel Anton Pears"
                className="w-full h-[480px] object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 flex items-end justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.3em] text-[#3B82F6]">en scène</div>
                  <div className="font-display text-3xl mt-1">Mariage 2025</div>
                </div>
                <div className="glass px-3 py-1.5 rounded-full text-xs uppercase tracking-widest">Live</div>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((f, i) => (
            <div
              key={f.title}
              className="reveal p-7 rounded-2xl border border-white/10 bg-[#0a0a0a] hover:border-[#3B82F6]/60 hover:shadow-[0_0_30px_rgba(59,130,246,0.15)] transition-all duration-500"
              style={{ transitionDelay: `${i * 80}ms` }}
              data-testid={`feature-card-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#3B82F6]/10 border border-[#3B82F6]/30 flex items-center justify-center text-[#3B82F6]">
                <f.icon size={20} />
              </div>
              <div className="font-display text-xl mt-5">{f.title}</div>
              <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-8 border-t border-white/10 pt-14">
          {stats.map((s, i) => (
            <Stat key={s.label} {...s} idx={i} />
          ))}
        </div>

        {/* Zones */}
        <div className="mt-16 reveal">
          <div className="text-xs uppercase tracking-[0.4em] text-[#3B82F6] mb-5">Zones d'intervention</div>
          <div className="flex flex-wrap gap-3">
            {SITE.zones.map((z) => (
              <div key={z.code} className="glass px-5 py-3 rounded-full inline-flex items-center gap-3" data-testid={`zone-${z.code}`}>
                <span className="font-display text-2xl text-[#3B82F6]">{z.code}</span>
                <span className="text-sm text-zinc-300">{z.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
